import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import QRCode from 'qrcode';
import { PageTitleService } from '../../core/services/page-title.service';
import { InoutService } from '../../core/services/inout.service';
import { FrontLogService, createRequestId } from '../../core/services/front-log.service';

function normalizeSeconds(value: any, fallback: number): number {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) : fallback;
}

@Component({
    selector: 'app-inout-qr', imports: [],
    templateUrl: './inout-qr.component.html'
})
export class InoutQrComponent implements OnInit, OnDestroy {
  private pageTitle = inject(PageTitleService);
  private inoutService = inject(InoutService);
  private log = inject(FrontLogService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private destroyed = false;
  private visibilityVersion = 0;
  private retrySeconds = 10;
  private readonly onVisibility = () => {
    ++this.visibilityVersion;
    if (this.destroyed || !this.isReady) return;
    this.clearCountdown('qrExpiresIn');
    this.initCanvas();
    this.disableUpdate = true;
    this.startCountdown(this.retrySeconds, 'disableButtonIn', () => { this.disableUpdate = !this.isReady || document.hidden; });
  };
  private timers: Partial<Record<'qrExpiresIn' | 'disableButtonIn', ReturnType<typeof setTimeout>>> = {};

  isReady = false;
  isUpdating = false;
  disableUpdate = true;
  showInitialLogo = true;
  showCode: number | null = null;
  qrExpiresIn = 0;
  disableButtonIn = 0;

  async ngOnInit(): Promise<void> {
    document.addEventListener('visibilitychange', this.onVisibility);
    this.pageTitle.set('', '入退館QR');
    this.log.event('qr.screen_init_start');
    let stage = 'config_check';
    try {
      const configured = await this.inoutService.isConfiguredQr();
      if (this.destroyed) return;
      this.log.event('qr.config_result', { configured });
      if (!configured) {
        this.log.warn('qr.config_unavailable', 'QR設定を確認できませんでした。');
        await this.router.navigate(['/top']);
        return;
      }
      stage = 'canvas_init';
      this.initCanvas();
      this.isReady = true;
      this.disableUpdate = false;
      this.log.event('qr.screen_ready');
    } catch (error) {
      if (this.destroyed) return;
      this.log.captureException('qr.screen_init_failed', error, { stage });
      this.toastr.error('QR画面の初期化に失敗しました。画面を再読み込みしてください。', '', { timeOut: 5000 });
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.destroyed = true;
    this.clearCountdown('qrExpiresIn');
    this.clearCountdown('disableButtonIn');
  }

  async updateQR(isEntry: number): Promise<void> {
    const entry = Number(isEntry) === 1 ? 1 : 0;
    this.log.event('qr.button_tap', { entry, isReady: this.isReady, disabled: this.disableUpdate, updating: this.isUpdating });
    if (this.destroyed || !this.isReady || this.disableUpdate || this.isUpdating) return;
    this.isUpdating = true;
    this.disableUpdate = true;
    this.disableButtonIn = 0;
    this.clearCountdown('qrExpiresIn');
    let retryInterval = 10;
    let stage = 'canvas_init';
    let requestId: string | undefined;
    const startedAt = performance.now();
    const visibilityVersion = this.visibilityVersion;
    try {
      // Clear the previous QR before requesting another one, including on failure.
      const canvas = this.initCanvas();
      requestId = createRequestId();
      stage = 'api_request';
      this.log.event('qr.request_start', { requestId, entry });
      const result = await this.inoutService.getQR(requestId, entry);
      if (this.destroyed) return;
      if (document.hidden || visibilityVersion !== this.visibilityVersion) return;
      retryInterval = normalizeSeconds(result?.interval ?? result?.data?.interval, 10);
      this.retrySeconds = retryInterval;
      const qrCode = result?.qrCode ?? result?.qr_code;
      const expire = normalizeSeconds(result?.expire ?? result?.expiresIn ?? result?.expires_in, 0);
      if (!qrCode) {
        const detail = result?.data?.error?.error ?? result?.error?.error ?? result?.message ?? result?.error ?? result;
        const message = typeof detail === 'string' ? detail : 'QRコードなし';
        this.log.warn('qr.request_failed', message, { requestId, entry, status: result?.status, elapsedMs: performance.now() - startedAt });
        this.toastr.error(`QRコード取得に失敗しました。[${message}]`, '', { timeOut: 5000 });
        return;
      }
      this.log.event('qr.request_success', { requestId, entry, expire, interval: retryInterval, elapsedMs: performance.now() - startedAt });
      stage = 'qr_draw';
      this.log.event('qr.draw_start', { requestId, entry });
      await this.drawQRCode(canvas, qrCode);
      if (this.destroyed) return;
      if (document.hidden || visibilityVersion !== this.visibilityVersion) { this.initCanvas(); return; }
      this.applySquareCanvasStyle(canvas);
      this.showInitialLogo = false;
      this.showCode = entry;
      this.log.event('qr.draw_success', { requestId, entry, elapsedMs: performance.now() - startedAt });
      this.startCountdown(Math.max(0, expire - (performance.now() - startedAt) / 1000), 'qrExpiresIn', () => {
        this.initCanvas();
        this.log.event('qr.expired');
        this.toastr.warning('QRコードの有効期限が切れました。', '', { timeOut: 5000 });
      });
    } catch (error: any) {
      if (this.destroyed) return;
      this.showCode = null;
      this.showInitialLogo = true;
      this.clearCountdown('qrExpiresIn');
      this.log.captureException('qr.flow_failed', error, { requestId, entry, stage, elapsedMs: performance.now() - startedAt });
      this.toastr.error(`QRコード生成に失敗しました。[${error?.message || '不明なエラー'}]`, '', { timeOut: 5000 });
    } finally {
      this.isUpdating = false;
      if (!this.destroyed) this.startCountdown(retryInterval, 'disableButtonIn', () => { this.disableUpdate = !this.isReady || document.hidden; });
    }
  }

  formatTime(seconds: number): string {
    const value = normalizeSeconds(seconds, 0);
    return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
  }

  private startCountdown(seconds: number, key: 'qrExpiresIn' | 'disableButtonIn', callback: () => void): void {
    this.clearCountdown(key);
    const endTime = performance.now() + seconds * 1000;
    const tick = () => {
      if (this.destroyed) return;
      this[key] = Math.max(0, Math.ceil((endTime - performance.now()) / 1000));
      if (this[key] > 0) this.timers[key] = setTimeout(tick, 1000);
      else { delete this.timers[key]; callback(); }
    };
    tick();
  }

  private clearCountdown(key: 'qrExpiresIn' | 'disableButtonIn'): void {
    clearTimeout(this.timers[key]);
    delete this.timers[key];
    this[key] = 0;
  }

  private initCanvas(): HTMLCanvasElement {
    this.showCode = null;
    this.showInitialLogo = true;
    const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement | null;
    if (!canvas) throw new Error('QRコードの表示領域を初期化できませんでした。');
    canvas.width = canvas.height = 800;
    this.applySquareCanvasStyle(canvas);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('QRコードの表示領域を初期化できませんでした。');
    context.clearRect(0, 0, canvas.width, canvas.height);
    return canvas;
  }

  private drawQRCode(canvas: HTMLCanvasElement, value: string): Promise<void> {
    return QRCode.toCanvas(canvas, value, { margin: 2, color: { dark: '#033', light: '#0000' }, scale: 16, width: 800, errorCorrectionLevel: 'H' });
  }

  private applySquareCanvasStyle(canvas: HTMLCanvasElement): void {
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.style.maxWidth = '100%';
    canvas.style.maxHeight = 'none';
    canvas.style.aspectRatio = '1 / 1';
  }
}
